import React, { useState, useEffect } from 'react';

import { useQuery, useMutation } from '@apollo/client';

import { Headline } from '@xingternal/typography';
import { FormattedMessage } from 'react-intl';

import { ImageUpload, getBase64Image } from '@xingternal/pages-common';

import ORIGINAL_COVER_QUERY from './OriginalCover.graphql';
import ORIGINAL_LOGO_QUERY from './OriginalLogo.graphql';

import UPDATE_LOGO from '../Logo/UpdateLogo.graphql';
import UPDATE_COVER from '../Cover/UpdateCover.graphql';

import WarningBox from '@xingternal/pages-warning';
import { ShowErrorProps } from '@xingternal/pages-page-error';

import Skeleton from './Skeleton';

import styles from '../edit.css';
import { ImageUploadCopy } from '@xingternal/pages-common/components/ImageUpload/ImageUpload';

type UpdateCropContainerProps = {
  isCover?: boolean;
  descriptionCopyKey?: string;
  onCropReady?: () => void;
  setDataChanged: (param: boolean) => void;
  aspectRatio?: number;
  setNewLogo?: (image: any) => void;
  setNewCover?: (image: any) => void; // optional
  newOriginal?: string | null;
  onDragCrop?: () => void;
  onDiscard: () => void;
  pageSlug: string;
  showError: ShowErrorProps;
  closeOmview: () => void;
  imageType: string;
  onPublish?: any;
  imageUploadCopy?: ImageUploadCopy;
};

const UpdateCropContainer = ({
  isCover,
  descriptionCopyKey,
  onCropReady,
  aspectRatio,
  setNewLogo,
  setNewCover,
  newOriginal,
  onDragCrop,
  onDiscard,
  pageSlug,
  closeOmview,
  imageType,
  showError,
  setDataChanged,
  onPublish,
  imageUploadCopy,
}: UpdateCropContainerProps) => {
  const [imageBase64, setImageBase64] = useState(newOriginal);

  const [updateLogo] = useMutation(UPDATE_LOGO);
  const [updateCover] = useMutation(UPDATE_COVER);

  const dimensions =
    imageType === 'cover'
      ? [{ width: 1024, height: 1024, reference: imageType }]
      : [{ width: 310, height: 310, reference: imageType }];

  const { data, loading, refetch } = useQuery(
    imageType === 'cover' ? ORIGINAL_COVER_QUERY : ORIGINAL_LOGO_QUERY,
    {
      fetchPolicy: 'no-cache',
      variables: {
        id: pageSlug,
        dimensions,
      },
    }
  );

  const originalImage =
    imageType === 'cover'
      ? data?.entityPageEX?.originalCoverImage?.[0]?.url
      : data?.entityPageEX?.originalLogoImage?.[0]?.url;

  const onUpload = async (
    pageSlug: string,
    uploadId: string,
    imageType: any,
    handleClose: any,
    image: any
  ) => {
    if (imageType === 'cover' && setNewCover) {
      await updateCover({
        variables: {
          id: pageSlug,
          coverMediaType: 'IMAGE',
          coverMediaUploadId: uploadId,
        },
      });
      setNewCover(image);
      setDataChanged(false);
    } else {
      await updateLogo({
        variables: {
          id: pageSlug,
          logoUploadId: uploadId,
        },
      });
      setNewLogo && setNewLogo(image);
    }

    onPublish && onPublish();
    handleClose();
  };

  useEffect(() => {
    if (originalImage && !imageBase64) {
      getBase64Image({
        url: originalImage,
        callback: (base64Img) => setImageBase64(base64Img),
        onError: onerror,
      });
    }
  }, [originalImage]);

  if (loading || !imageBase64) {
    return (
      <>
        <Headline size={'xxlarge'} className={styles.uploadHeadline}>
          <FormattedMessage
            id="EP_CHOOSE_SECTION"
            defaultMessage="EP_CHOOSE_SECTION"
          />
        </Headline>
        <Skeleton isCover={isCover} />
      </>
    );
  }

  if (!originalImage && !imageBase64) {
    return (
      <>
        <Headline size={'xxlarge'} className={styles.uploadHeadline}>
          <FormattedMessage
            id="EP_CHOOSE_SECTION"
            defaultMessage="EP_CHOOSE_SECTION"
          />
        </Headline>
        <WarningBox
          headerText="EP_ERROR_HEADER"
          bodyText="EP_ERROR_BODY"
          buttonText="EP_ERROR_RELOAD_CTA"
          onClick={() => refetch()}
        />
      </>
    );
  }

  return (
    <ImageUpload
      descriptionCopyKey={descriptionCopyKey}
      onCropReady={onCropReady}
      onDragCrop={onDragCrop}
      onDiscard={onDiscard}
      showError={showError}
      previewImage={imageBase64}
      onPublish={(uploadId, image, _) =>
        onUpload(pageSlug, uploadId, imageType, closeOmview, image)
      }
      aspectRatio={aspectRatio}
      onCancel={closeOmview}
      imageUploadCopy={imageUploadCopy}
    />
  );
};

export default UpdateCropContainer;
